import React, { useState } from "react";
import { message } from "antd";
import { PostData } from "../../Service/service";

const STEPS = [
  { id: 1, label: "Email" },
  { id: 2, label: "Verify" },
  { id: 3, label: "New password" },
];

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [serverMessage, setServerMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function submitEmail(e) {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setError("");
    setIsLoading(true);
    PostData("/forgotpassword", { email })
      .then((resp) => {
        setIsLoading(false);
        if (resp !== false) {
          setServerMessage(typeof resp === "string" ? resp : "");
          setStep(2);
        } else {
          setError("No account found with this email address.");
        }
      })
      .catch(() => {
        setIsLoading(false);
        setError("Something went wrong. Please try again.");
      });
  }

  function submitOtp(e) {
    e.preventDefault();
    if (!otp) {
      setError("Please enter the verification code.");
      return;
    }
    setError("");
    setIsLoading(true);
    PostData("/forgotpassword/otp", { otp, email })
      .then((resp) => {
        setIsLoading(false);
        if (resp === true) {
          setServerMessage("");
          setStep(3);
        } else {
          setError("That code didn't match. Please try again.");
        }
      })
      .catch(() => {
        setIsLoading(false);
        setError("Something went wrong. Please try again.");
      });
  }

  function submitPassword(e) {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Use at least 6 characters for your new password.");
      return;
    }

    setError("");
    setIsLoading(true);
    PostData("/forgotpassword/final", { email, password: newPassword, otp })
      .then((resp) => {
        setIsLoading(false);
        if (resp === true) {
          message.success("Password successfully changed");
          window.location.href = "/login";
        } else {
          setError("Something went wrong. Please try again in a moment.");
        }
      })
      .catch(() => {
        setIsLoading(false);
        setError("Something went wrong. Please try again in a moment.");
      });
  }

  function goToStep(target) {
    setError("");
    setServerMessage("");
    setStep(target);
  }

  return (
    <div className="min-h-screen w-screen flex bg-surface">
      {/* Brand panel — hidden on mobile */}
      <aside className="hidden lg:flex relative w-1/2 flex-col justify-between p-12 overflow-hidden text-white">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #0f766e 0%, #115e59 45%, #134e4a 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(45,212,191,0.35), transparent 45%), radial-gradient(circle at 80% 90%, rgba(20,184,166,0.25), transparent 50%)",
          }}
        />

        <div className="relative flex items-center gap-3">
          <img
            src="/logo512.png"
            alt="Shishu Sarothi"
            className="w-10 h-10 rounded-lg bg-white/10 p-1"
          />
          <div className="leading-tight">
            <div className="text-xs uppercase tracking-widest text-brand-100/80">
              Shishu Sarothi
            </div>
            <div className="text-base font-semibold">MIS Panel</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-4xl font-semibold leading-tight text-white">
            Let's get you back in.
          </h2>
          <p className="mt-4 text-base text-brand-50/80 leading-relaxed">
            We'll verify your email with a one-time code and help you set a new
            password — it only takes a minute.
          </p>
        </div>

        <div className="relative text-xs text-brand-100/70">
          &copy; {new Date().getFullYear()} Shishu Sarothi. All rights reserved.
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {/* Mobile brand mark */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img
              src="/logo512.png"
              alt="Shishu Sarothi"
              className="w-9 h-9 rounded-lg"
            />
            <div className="leading-tight">
              <div className="text-xxs uppercase tracking-widest text-ink-500">
                Shishu Sarothi
              </div>
              <div className="text-sm font-semibold text-ink-900">MIS Panel</div>
            </div>
          </div>

          <Stepper current={step} />

          {step === 1 && (
            <StepShell
              title="Reset your password"
              subtitle="Enter the email tied to your account. We'll send you a verification code."
              error={error}
            >
              <form onSubmit={submitEmail} className="space-y-4" noValidate>
                <Field
                  id="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@shishusarothi.org"
                  value={email}
                  onChange={setEmail}
                  disabled={isLoading}
                  hasError={!!error}
                  autoFocus
                />
                <SubmitButton isLoading={isLoading} loadingLabel="Sending code…">
                  Send verification code
                </SubmitButton>
              </form>
            </StepShell>
          )}

          {step === 2 && (
            <StepShell
              title="Check your inbox"
              subtitle={
                <>
                  We sent a verification code to{" "}
                  <span className="font-medium text-ink-900">{email}</span>.
                  Enter it below to continue.
                </>
              }
              info={serverMessage}
              error={error}
            >
              <form onSubmit={submitOtp} className="space-y-4" noValidate>
                <Field
                  id="otp"
                  label="Verification code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={setOtp}
                  disabled={isLoading}
                  hasError={!!error}
                  autoFocus
                />
                <SubmitButton isLoading={isLoading} loadingLabel="Verifying…">
                  Verify code
                </SubmitButton>
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  className="block w-full text-center text-sm text-ink-500 hover:text-ink-700 transition-colors"
                >
                  Use a different email
                </button>
              </form>
            </StepShell>
          )}

          {step === 3 && (
            <StepShell
              title="Set a new password"
              subtitle="Choose a strong password you haven't used before."
              error={error}
            >
              <form onSubmit={submitPassword} className="space-y-4" noValidate>
                <PasswordField
                  id="new_password"
                  label="New password"
                  show={showPassword}
                  onToggleShow={() => setShowPassword((v) => !v)}
                  value={newPassword}
                  onChange={setNewPassword}
                  disabled={isLoading}
                  hasError={!!error}
                  autoFocus
                />
                <div>
                  <label
                    htmlFor="confirm_password"
                    className="block text-xs font-medium text-ink-700 mb-1.5"
                  >
                    Confirm new password
                  </label>
                  <input
                    id="confirm_password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className="ss-input"
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    aria-invalid={!!error}
                  />
                </div>
                <SubmitButton isLoading={isLoading} loadingLabel="Updating…">
                  Update password
                </SubmitButton>
              </form>
            </StepShell>
          )}

          <div className="mt-8 text-center">
            <a
              href="/login"
              className="text-sm text-brand-700 hover:text-brand-600 font-medium transition-colors"
            >
              ← Back to sign in
            </a>
          </div>

          <div className="lg:hidden mt-10 text-center text-xxs text-ink-400">
            &copy; {new Date().getFullYear()} Shishu Sarothi
          </div>
        </div>
      </main>
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((s, idx) => {
        const isDone = current > s.id;
        const isActive = current === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xxs font-semibold transition-colors ${
                  isActive
                    ? "bg-brand-600 text-white"
                    : isDone
                    ? "bg-brand-100 text-brand-700"
                    : "bg-ink-100 text-ink-400"
                }`}
              >
                {isDone ? "✓" : s.id}
              </div>
              <span
                className={`text-xs font-medium hidden sm:inline ${
                  isActive
                    ? "text-ink-900"
                    : isDone
                    ? "text-ink-700"
                    : "text-ink-400"
                }`}
              >
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-px ${
                  current > s.id ? "bg-brand-200" : "bg-ink-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepShell({ title, subtitle, info, error, children }) {
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-ink-900">{title}</h1>
        <p className="mt-1 text-sm text-ink-500">{subtitle}</p>
      </div>

      {info && (
        <div className="mb-5 px-3 py-2.5 rounded-md bg-brand-50 border border-brand-100 text-sm text-brand-700">
          {info}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 text-sm text-red-700"
        >
          <svg
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 102 0V6zm-1 8a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {children}
    </>
  );
}

function Field({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  inputMode,
  disabled,
  hasError,
  autoFocus,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-medium text-ink-700 mb-1.5"
      >
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className="ss-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!hasError}
        autoFocus={autoFocus}
      />
    </div>
  );
}

function PasswordField({
  id,
  label,
  show,
  onToggleShow,
  value,
  onChange,
  disabled,
  hasError,
  autoFocus,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="block text-xs font-medium text-ink-700">
          {label}
        </label>
        <button
          type="button"
          onClick={onToggleShow}
          className="text-xs text-ink-500 hover:text-ink-700 transition-colors"
          tabIndex={-1}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
      <input
        id={id}
        type={show ? "text" : "password"}
        autoComplete="new-password"
        className="ss-input"
        placeholder="Enter a new password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!hasError}
        autoFocus={autoFocus}
      />
    </div>
  );
}

function SubmitButton({ isLoading, loadingLabel, children }) {
  return (
    <button
      type="submit"
      disabled={isLoading}
      className="ss-btn-primary w-full py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          {loadingLabel}
        </>
      ) : (
        children
      )}
    </button>
  );
}
